import { isEmpty } from 'lodash-es';

import {
  getLinearAccessors,
  type AnyLinearAccessors,
  type AnyLinearDatapoint,
} from './get-linear-accessors.js';
import { getCurrentScale } from './get-scale.js';
import { distanceBetweenTwoPoints } from './query-closest-quadtree.js';
import { relAccessor } from './value-representation.js';
import { DEFAULT_DATA_POINT_RADIUS } from '../../core/constants.js';
import type { Point } from '../../core/types/point.js';
import { isBandScale } from '../../core/utils/detect-scale.js';
import { safeGet } from '../../core/utils/safe-get.js';
import type {
  AreaSeriesQueryResult,
  DotSeriesQueryResult,
  LineSeriesQueryResult,
  SeriesQueryResult,
} from '../context/XYChartOverlayPerformance.context.js';
import type { AxisIdToScales } from '../context/XYChartScales.context.js';
import {
  type AnyLinearSeries,
  type AnyXYChartScale,
  type AreaSeriesWithDatapoints,
  type AxisIdToScaleDomain,
  type DatapointMetadata,
  type DotSeriesWithDatapoints,
  type LineSeriesWithDatapoints,
  type MapDatapoints,
  type SeriesToMetadata,
  type XYChartSeriesWithDatapoints,
} from '../types/xy-chart-internals.js';

type RelAccessorFn = ((value: number) => number) | undefined;

type ScaleBounds = {
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
};

type Accumulator = {
  distance: number;
  datapoint: AnyLinearDatapoint;
  series: AnyLinearSeries;
};

export function getDistance(
  datapoint: AnyLinearDatapoint,
  xScale: AnyXYChartScale,
  yScale: AnyXYChartScale,
  relativePosition: Point,
  xAccessor: (scale: AnyXYChartScale) => (d: AnyLinearDatapoint) => number,
  yAccessor: (scale: AnyXYChartScale) => (d: AnyLinearDatapoint) => number,
) {
  const xPixel = xAccessor(xScale)(datapoint);
  const yPixel = yAccessor(yScale)(datapoint);

  return distanceBetweenTwoPoints(
    relativePosition.x,
    xPixel,
    relativePosition.y,
    yPixel,
  );
}

function calculateScalesBounds(
  xScale: AnyXYChartScale,
  yScale: AnyXYChartScale,
): ScaleBounds {
  const [minWidth, maxWidth] = xScale.range();
  const yScaleRange = yScale.range();
  const [minHeight, maxHeight] = isBandScale(yScale)
    ? [yScaleRange[0], yScaleRange[1]]
    : [yScaleRange[1], yScaleRange[0]];

  return { minWidth, maxWidth, minHeight, maxHeight };
}

/**
 * Get the appropriate accessors for a series based on its type.
 *
 * @param seriesType - The type of the series.
 * @param seriesToMetadata - Map from series to metadata.
 * @param currentSeries - The current series.
 * @param xScale - The X scale.
 * @param yScale - The Y scale.
 * @param datapointToMetadata - Map with datapoint metadata.
 * @returns Object with xAccessor and yAccessor functions.
 */
function getAccessorsForSeries(
  seriesType: AnyLinearSeries['type'],
  seriesToMetadata: SeriesToMetadata,
  currentSeries: AnyLinearSeries,
  datapointToMetadata: MapDatapoints,
  xRelAccessor: RelAccessorFn,
  yRelAccessor: RelAccessorFn,
): AnyLinearAccessors {
  const metadata = safeGet(seriesToMetadata, currentSeries);

  return getLinearAccessors(
    seriesType,
    metadata,
    datapointToMetadata,
    xRelAccessor,
    yRelAccessor,
  );
}

/**
 * Check if a datapoint is within the scale bounds.
 *
 * @param xPixel - X coordinate of the datapoint.
 * @param yPixel - Y coordinate of the datapoint.
 * @param bounds - Scale bounds.
 * @returns True if the datapoint is out of bounds.
 */
function isDatapointOutOfBounds(
  xPixel: number,
  yPixel: number,
  bounds: ScaleBounds,
): boolean {
  const { minWidth, maxWidth, minHeight, maxHeight } = bounds;
  return (
    xPixel < minWidth ||
    xPixel > maxWidth ||
    yPixel < minHeight ||
    yPixel > maxHeight
  );
}

/**
 * Find the closest datapoint within a single series.
 *
 * @param data - Array of datapoints to search.
 * @param xScale - X scale.
 * @param yScale - Y scale.
 * @param relativePosition - The position to find closest datapoint to.
 * @param accessors - Accessor functions.
 * @param bounds - Scale bounds.
 * @param currentAccumulator - Current best match to compare against.
 * @returns Updated accumulator with the closest datapoint, or undefined if none found within radius.
 */
function findClosestInSeriesData(
  data: AnyLinearDatapoint[],
  xScale: AnyXYChartScale,
  yScale: AnyXYChartScale,
  relativePosition: Point,
  accessors: AnyLinearAccessors,
  bounds: ScaleBounds,
  currentAccumulator: Accumulator,
): Accumulator | undefined {
  let bestInSeries = currentAccumulator;

  let datapointIndex = 0;
  while (datapointIndex < data.length) {
    const datapoint = data[datapointIndex];

    const xPixel = accessors.xAccessor(xScale)(datapoint);
    const yPixel = accessors.yAccessor(yScale)(datapoint);

    if (isDatapointOutOfBounds(xPixel, yPixel, bounds)) {
      datapointIndex += 1;
      continue;
    }

    const distance = getDistance(
      datapoint,
      xScale,
      yScale,
      relativePosition,
      accessors.xAccessor,
      accessors.yAccessor,
    );

    if (distance < bestInSeries.distance) {
      bestInSeries = {
        distance,
        datapoint,
        series: currentAccumulator.series,
      };
    }

    if (distance <= DEFAULT_DATA_POINT_RADIUS) {
      return bestInSeries;
    }

    datapointIndex += 1;
  }

  return bestInSeries.distance < currentAccumulator.distance
    ? bestInSeries
    : undefined;
}

export function getClosestDatapoint(
  relativePosition: Point,
  series: AnyLinearSeries[],
  axisIdToScales: AxisIdToScales,
  seriesToMetadata: SeriesToMetadata,
  datapointToMetadata: MapDatapoints,
  axisIdToScaleDomain?: AxisIdToScaleDomain,
) {
  if (!series.length) {
    return undefined;
  }

  const initialAccumulator: Accumulator = {
    distance: Infinity,
    datapoint: {} as AnyLinearDatapoint,
    series: {} as AnyLinearSeries,
  };

  let bestMatch = initialAccumulator;

  let seriesIndex = series.length - 1;
  while (seriesIndex >= 0) {
    const currentSeries = series[seriesIndex];
    const { data, xAxisId, yAxisId, type } = currentSeries;

    const xScale = getCurrentScale(axisIdToScales, xAxisId);
    const yScale = getCurrentScale(axisIdToScales, yAxisId);
    const seriesMetadata = safeGet(seriesToMetadata, currentSeries);
    const xRelAccessor = axisIdToScaleDomain
      ? relAccessor(seriesMetadata.xAxis, axisIdToScaleDomain)
      : undefined;
    const yRelAccessor = axisIdToScaleDomain
      ? relAccessor(seriesMetadata.yAxis, axisIdToScaleDomain)
      : undefined;
    const accessors = getAccessorsForSeries(
      type,
      seriesToMetadata,
      currentSeries,
      datapointToMetadata,
      xRelAccessor,
      yRelAccessor,
    );

    const bounds = calculateScalesBounds(xScale, yScale);

    const seriesAccumulator: Accumulator = {
      ...initialAccumulator,
      series: currentSeries,
    };

    const updatedAccumulator = findClosestInSeriesData(
      data,
      xScale,
      yScale,
      relativePosition,
      accessors,
      bounds,
      seriesAccumulator,
    );

    if (updatedAccumulator) {
      bestMatch = updatedAccumulator;
      if (updatedAccumulator.distance <= DEFAULT_DATA_POINT_RADIUS) {
        break;
      }
    }

    seriesIndex -= 1;
  }

  if (isEmpty(bestMatch.datapoint) || isEmpty(bestMatch.series)) {
    return undefined;
  }

  return {
    datapoint: bestMatch.datapoint,
    series: bestMatch.series,
    distance: bestMatch.distance,
  };
}

export function buildQueryGetClosestPoint(
  axisIdToScales: AxisIdToScales,
  seriesToMetadata: SeriesToMetadata,
  datapointToMetadata: MapDatapoints,
  axisIdToScaleDomain?: AxisIdToScaleDomain,
) {
  function query(
    arrayOfSeries: DotSeriesWithDatapoints[],
    position: Point,
  ): DotSeriesQueryResult | undefined;
  function query(
    arrayOfSeries: LineSeriesWithDatapoints[],
    position: Point,
  ): LineSeriesQueryResult | undefined;
  function query(
    arrayOfSeries: AreaSeriesWithDatapoints[],
    position: Point,
  ): AreaSeriesQueryResult | undefined;
  function query(arrayOfSeries: AnyLinearSeries[], position: Point) {
    if (isEmpty(arrayOfSeries)) {
      return undefined;
    }

    const closest = getClosestDatapoint(
      position,
      arrayOfSeries,
      axisIdToScales,
      seriesToMetadata,
      datapointToMetadata,
      axisIdToScaleDomain,
    );

    if (!closest) {
      return undefined;
    }

    return {
      type: closest.series.type,
      distance: closest?.distance,
      datapoint: closest?.datapoint,
      series: closest?.series,
    } as DotSeriesQueryResult | LineSeriesQueryResult | AreaSeriesQueryResult;
  }

  return query;
}

function serializeBoundingBoxValue(value: unknown): string {
  if (value === undefined) {
    return 'undefined';
  }
  if (value instanceof Date) {
    return `d:${value.getTime()}`;
  }
  if (typeof value === 'number') {
    return `n:${value}`;
  }
  if (typeof value === 'string') {
    return `s:${value}`;
  }
  return `other:${String(value)}`;
}

function boundingBoxKey(
  datapoint: Record<string, unknown>,
  metadata?: DatapointMetadata,
): string {
  const accumulator = metadata?.accumulator;
  const baseline = metadata?.baseline;

  const x0 = datapoint.x0;
  let x1 = datapoint.x1;
  const y0 = datapoint.y0;
  let y1 = datapoint.y1;

  if (accumulator !== undefined) {
    if (baseline === 'x') {
      y1 =
        typeof datapoint.y1 === 'number'
          ? datapoint.y1 + accumulator
          : datapoint.y1;
    } else if (baseline === 'y') {
      x1 =
        typeof datapoint.x1 === 'number'
          ? datapoint.x1 + accumulator
          : datapoint.x1;
    }
  }

  return [x0, x1, y0, y1].map(serializeBoundingBoxValue).join('|');
}

function exactMatchKey(
  series: XYChartSeriesWithDatapoints,
  datapoint: Record<string, unknown>,
  metadata?: DatapointMetadata,
): string {
  return `${series.xAxisId}|${series.yAxisId}|${boundingBoxKey(datapoint, metadata)}`;
}

export type ExactMatchIndex = Map<string, SeriesQueryResult[]>;

export function buildExactMatchIndex(
  allSeries: XYChartSeriesWithDatapoints[],
  datapointMetadataMap?: MapDatapoints,
): ExactMatchIndex {
  const index: ExactMatchIndex = new Map();

  for (const series of allSeries) {
    for (const dp of series.data) {
      const metadata = datapointMetadataMap?.get(dp);
      if (metadata?.isGap) {
        continue;
      }
      const key = exactMatchKey(
        series,
        dp as Record<string, unknown>,
        metadata,
      );
      const entry = {
        type: series.type,
        distance: 0,
        datapoint: dp,
        series,
      } as SeriesQueryResult;
      const existing = index.get(key);
      if (existing) {
        existing.push(entry);
      } else {
        index.set(key, [entry]);
      }
    }
  }

  return index;
}

export function findExactMatchFromIndex(
  preferred: SeriesQueryResult,
  index: ExactMatchIndex,
  datapointMetadataMap?: MapDatapoints,
): SeriesQueryResult[] {
  const metadata = datapointMetadataMap?.get(
    preferred.datapoint as AnyLinearDatapoint,
  );
  const key = exactMatchKey(
    preferred.series,
    preferred.datapoint as Record<string, unknown>,
    metadata,
  );
  const matches = index.get(key);
  return matches && matches.length > 0 ? matches : [preferred];
}

export function findExactMatchDatapoints(
  preferred: SeriesQueryResult,
  allSeries: XYChartSeriesWithDatapoints[],
): SeriesQueryResult[] {
  return findExactMatchFromIndex(preferred, buildExactMatchIndex(allSeries));
}
